import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthFormShell from '../../components/AuthFormShell.jsx'
import PasswordChecklist, {
  isPasswordStrongEnough,
} from '../../components/PasswordChecklist.jsx'
import PasswordField from '../../components/PasswordField.jsx'
import Logo from '../../components/Logo.jsx'
import { useAuth } from '../../context/AuthContext.jsx'
import './authTitam.css'

export default function ResetPassword() {
  const navigate = useNavigate()
  const {
    exchangeRecoveryCode,
    isRecoverySession,
    loading: authLoading,
    updatePassword,
  } = useAuth()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [exchanging, setExchanging] = useState(false)

  const passwordStrongEnough = useMemo(
    () => isPasswordStrongEnough(password),
    [password]
  )

  useEffect(() => {
    if (!isRecoverySession) {
      setMessage('')
    }
  }, [isRecoverySession])

  useEffect(() => {
    if (!message) {
      return undefined
    }

    const timer = window.setTimeout(() => {
      navigate('/login', {
        replace: true,
        state: {
          resetDone: true,
        },
      })
    }, 2500)

    return () => {
      window.clearTimeout(timer)
    }
  }, [message, navigate])

  useEffect(() => {
    let cancelled = false

    async function maybeExchangeCode() {
      const search = new URLSearchParams(window.location.search)
      const code = search.get('code')

      if (!code || isRecoverySession) {
        return
      }

      setExchanging(true)
      setError('')

      const { error: exchangeError } = await exchangeRecoveryCode(code)

      if (cancelled) {
        return
      }

      if (exchangeError) {
        setError(exchangeError.message || 'Nao foi possivel validar o link de recuperacao.')
      }

      setExchanging(false)
    }

    void maybeExchangeCode()

    return () => {
      cancelled = true
    }
  }, [exchangeRecoveryCode, isRecoverySession])

  async function handleSubmit(event) {
    event.preventDefault()
    setError('')
    setMessage('')

    if (!isRecoverySession) {
      setError('Abra esta tela a partir do link enviado por e-mail para redefinir sua senha.')
      return
    }

    if (!passwordStrongEnough) {
      setError('A nova senha precisa cumprir todos os requisitos exibidos abaixo do campo.')
      return
    }

    if (password !== confirmPassword) {
      setError('A confirmacao de senha precisa ser igual a nova senha.')
      return
    }

    setLoading(true)
    const { error: updateError } = await updatePassword(password)

    if (updateError) {
      setError(updateError.message || 'Nao foi possivel redefinir sua senha.')
      setLoading(false)
      return
    }

    setLoading(false)
    setMessage('Senha atualizada com sucesso. Redirecionando para o login...')
  }

  return (
    <AuthFormShell
      showcaseVariant="titam"
      showcaseTitle="Defina uma nova senha."
      showcaseDescription="Escolha uma senha forte para concluir a recuperação e voltar a acessar o painel."
    >
      <form className="auth-form-wrap auth-titam-form" onSubmit={handleSubmit}>
        <span className="auth-form-logo">
          <Logo height={14} />
        </span>
        <h1 className="auth-title">Nova senha</h1>
        <p className="auth-desc">
          Escolha uma nova senha para concluir a recuperacao da conta.
        </p>

        {exchanging || authLoading ? (
          <div className="auth-panel soft">
            <div className="auth-panel-title">Validando link</div>
            <div className="auth-panel-text">
              Estamos confirmando sua sessao de recuperacao antes de liberar a troca de senha.
            </div>
          </div>
        ) : null}

        {!authLoading && !exchanging && !isRecoverySession ? (
          <div className="auth-warning">
            Esta tela precisa ser aberta a partir do link de recuperacao recebido por e-mail.
          </div>
        ) : null}

        {error ? <div className="auth-error">{error}</div> : null}
        {message ? <div className="auth-success">{message}</div> : null}

        {!message ? (
          <>
            <PasswordField
              id="reset-password"
              label="Nova senha"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              disabled={exchanging || authLoading}
              required
              hint="Use uma senha que cumpra os requisitos abaixo."
            />
            <PasswordChecklist password={password} />

            <PasswordField
              id="reset-password-confirm"
              label="Confirmar nova senha"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              disabled={exchanging || authLoading}
              required
            />

            <button
              className="btn btn-full auth-titam-submit"
              type="submit"
              disabled={loading || exchanging || authLoading}
            >
              {exchanging || authLoading
                ? 'Validando link...'
                : loading
                  ? 'Atualizando...'
                  : 'Salvar nova senha'}
            </button>
          </>
        ) : (
          <div className="auth-panel soft">
            <div className="auth-panel-title">Tudo certo</div>
            <div className="auth-panel-text">
              Sua nova senha foi salva e voce sera levado para a tela de login em instantes.
            </div>
          </div>
        )}

        {!message ? (
          <div className="auth-switch">
            Voltar para{' '}
            <button type="button" onClick={() => navigate('/login')}>
              login
            </button>
          </div>
        ) : null}
      </form>
    </AuthFormShell>
  )
}
